import React, { useContext, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiX,
  HiUpload,
  HiTrash,
  HiSave,
  HiDocumentDownload,
} from "react-icons/hi";
import { AuthContext } from "../context/AuthContext";
import { useDropzone } from "react-dropzone";
import EditContractForm from "./EditContractForm";

const ContractDetailsModal = ({ isOpen, onClose, contract, onUpdate }) => {
  const [formData, setFormData] = useState(contract);
  const [attachment, setAttachment] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const { authState } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const onDrop = useCallback((acceptedFiles) => {
    setAttachment(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleUpdate = async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/contracts/${contract._id}`,
        formData,
        {
          headers: { "x-auth-token": authState.token },
        }
      );
      toast.success(response.data.message);
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update contract");
    }
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/contracts/${contract._id}`,
        {
          headers: { "x-auth-token": authState.token },
        }
      );
      toast.success(response.data.message);
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete contract");
    }
  };

  const handleUpload = async () => {
    if (!attachment) {
      toast.error("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("attachment", attachment);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/attachments/contracts/${contract._id}/attachments`,
        formData,
        {
          headers: {
            "x-auth-token": authState.token,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success(response.data.message);
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to upload attachment"
      );
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/attachments/contracts/${contract._id}/attachments/${attachment._id}`,
        {
          headers: { "x-auth-token": authState.token },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download attachment");
    }
  };

  if (!isOpen) return null;

  if (authState.user.role !== "admin") {
    navigate("/accessdenied");
    return null;
  }

  const ConfirmationDialog = ({ action, onConfirm, onCancel }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Confirm {action}</h3>
        <p>Are you sure you want to {action.toLowerCase()} this contract?</p>
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200"
          >
            Confirm
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Contract Details
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition duration-200"
                >
                  <HiX size={24} />
                </button>
              </div>

              <EditContractForm
                formData={formData}
                handleChange={handleChange}
              />

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-2">Attachments</h3>
                <div className="space-y-2">
                  {formData.attachments &&
                    formData.attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-100 p-2 rounded"
                      >
                        <span className="text-sm truncate">
                          {attachment.fileName}
                        </span>
                        <button
                          onClick={() => handleDownload(attachment)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <HiDocumentDownload size={20} />
                        </button>
                      </div>
                    ))}
                </div>
                <div
                  {...getRootProps()}
                  className={`mt-4 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-gray-400 transition-colors ${
                    isDragActive ? "border-blue-500 bg-blue-50" : ""
                  }`}
                >
                  <div className="space-y-1 text-center">
                    <HiUpload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload a file</span>
                        <input {...getInputProps()} />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, DOCX up to 10MB
                    </p>
                  </div>
                </div>
                {attachment && (
                  <p className="mt-2 text-sm text-gray-500">
                    Selected file: {attachment.name}
                  </p>
                )}
              </div>
              <div className="mt-8 flex flex-wrap justify-end gap-4">
                {authState.user.role.includes("admin") && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setConfirmAction("Update")}
                      className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-200 flex items-center"
                    >
                      <HiSave className="mr-2" /> Update
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setConfirmAction("Delete")}
                      className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-200 flex items-center"
                    >
                      <HiTrash className="mr-2" /> Delete
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setConfirmAction("Upload")}
                      className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-200 flex items-center"
                    >
                      <HiUpload className="mr-2" /> Upload
                    </motion.button>
                  </>
                )}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-200"
                >
                  Close
                </motion.button>
              </div>
            </div>
          </motion.div>
          <AnimatePresence>
            {confirmAction && (
              <ConfirmationDialog
                action={confirmAction}
                onConfirm={() => {
                  if (confirmAction === "Update") handleUpdate();
                  else if (confirmAction === "Delete") handleDelete();
                  else if (confirmAction === "Upload") handleUpload();
                  setConfirmAction(null);
                }}
                onCancel={() => setConfirmAction(null)}
              />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContractDetailsModal;
